import { FormControl } from 'foundations-components/transitional/components/FormControl';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { fromJS } from 'immutable';
import PropTypes from 'prop-types';
import { useContext } from 'react';
import { ANALYTICS_FUNNEL, ATTRIBUTION } from 'reporting-data/constants/dataTypes';
import { CUSTOM_WIDGET_TYPES } from 'reporting-data/tsTypes/customWidgetTypes';
import { isConfigReport, isMultiConfigReport, isReportWithPlugins } from 'reporting-data/tsTypes/reportTypes';
import { UserInfoContext } from 'reporting-data/userInfo/userInfoContext';
// @ts-expect-error Untyped dependency
import makeDateRangeByType from 'reporting-data/lib/makeDateRangeByType';
import { FiscalYearContext } from 'reporting-ui-components/components/PlatformReport/FiscalYearProvider';
// @ts-expect-error Untyped dependency
import DateRangeLabel from 'reporting-ui-components/components/shared/DateRangeLabel';
// @ts-expect-error Untyped dependency
import DateRangePicker from 'reporting-ui-components/components/shared/DateRangePicker';
// @ts-expect-error Untyped dependency
import FullDatePeriodInput from 'reporting-ui-components/components/shared/DateRangePicker/FullDatePeriodInput';
import { DateActionTypes } from 'reporting-ui-components/constants/trackingConstants';
import { EVENTS } from 'reporting-ui-components/lib/events';
import { customWidgetTypeIs } from 'reporting-ui-components/components/customWidgets/utils';
// @ts-expect-error Untyped dependency
import getDefaultFrequency from 'reporting-ui-components/lib/getDefaultFrequency';
import useCloneTracker from 'reporting-ui-components/lib/useCloneTracker';
import { ReportType } from 'reporting-ui-components/types/ReportTypes';
import { View } from 'foundations-components/transitional/components/View';
import { getUseFiscalYear } from 'reporting-data/lib/fiscalYearUtils';
import { getDataType, getDateRangeFilter, getDateRangeFilterValue, getDisplayParams, getReportConfig } from 'reporting-data/report/configReportGetters';
import { getCustomWidgetType } from 'reporting-data/report/reportGetters';
import { isFiscalYearEnabled } from '../reportEditors/common/isFiscalYearEnabled';
import BasePopoverEditorComponent from './common/BasePopoverEditorComponent';
import { getValidFrequenciesForReport, hasFutureRangeTypeSupport, hasRelativeRangeTypeSupport, hasSecondaryDateRangeEditor, useLimitedDateRangeOptions } from './common/dateRangeHelpers';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const READ_ONLY_DATATYPES = [ANALYTICS_FUNNEL, ATTRIBUTION];
const DateRangeEditor = ({
  report,
  options,
  onChange,
  appUsageTracker,
  trackingAssetType,
  trackingApplicationName,
  trackingScreenName,
  isV2FilterExperience
}) => {
  const usageTracker = useCloneTracker(appUsageTracker);
  const reportConfig = getReportConfig(report);
  const dataType = getDataType(report);
  const readOnly = options.readOnly || READ_ONLY_DATATYPES.includes(dataType);
  const dateRangeFilter = getDateRangeFilter(reportConfig);
  const {
    gates
  } = useContext(UserInfoContext);
  const fiscalYearMonthName = useContext(FiscalYearContext);
  const limitedRangeTypeOptions = useLimitedDateRangeOptions(report);
  const dateRange = dateRangeFilter.get('value');
  const useFiscalYear = dateRangeFilter.get('useFiscalYear', false);
  const completeDateRange = makeDateRangeByType(dateRange.toJS());
  const DateRangeInput = customWidgetTypeIs(report, CUSTOM_WIDGET_TYPES.SALES_QUOTA) ? FullDatePeriodInput : DateRangePicker;
  const dateRangeInput = /*#__PURE__*/_jsx(DateRangeInput, Object.assign({
    buttonUse: "form",
    condensed: true
  }, dateRange.toJS(), {
    date: completeDateRange.date || completeDateRange.startDate,
    useFiscalYear: useFiscalYear,
    isFiscalYearEnabled: isFiscalYearEnabled(report, gates),
    onChange: (dateRangeValue, updatedUseFiscalYear = false) => {
      usageTracker.track(EVENTS.changeSrvDateFilters, {
        action: DateActionTypes.DATE_RANGE,
        assetType: trackingAssetType || 'report',
        applicationName: trackingApplicationName,
        whereScreen: trackingScreenName,
        isNewFilterExperience: isV2FilterExperience
      });
      const getUpdatedDateRange = configDateRange => fromJS(Object.assign({}, configDateRange.toJS(), {
        value: dateRangeValue,
        useFiscalYear: updatedUseFiscalYear
      }));
      let defaultFrequency = getDefaultFrequency(dateRangeValue, updatedUseFiscalYear, fiscalYearMonthName);
      const validFrequencies = getValidFrequenciesForReport(report);
      if (validFrequencies.length && !validFrequencies.includes(defaultFrequency)) {
        defaultFrequency = validFrequencies[0];
      }
      const getUpdateFrequency = frequency => frequency && defaultFrequency;
      onChange(report.updateIn(['config', 'filters', 'dateRange'], getUpdatedDateRange).updateIn(['config', 'frequency'], getUpdateFrequency).setIn(['config', 'useFiscalYearInAggregation'], updatedUseFiscalYear).updateIn(['reportConfigs'], configs => {
        if (isMultiConfigReport(report)) {
          return configs.map(config => config.updateIn(['filters', 'dateRange'], getUpdatedDateRange).update('frequency', getUpdateFrequency).set('useFiscalYearInAggregation', updatedUseFiscalYear));
        }
        return configs;
      }));
    },
    showRelativeRangeTypes: hasRelativeRangeTypeSupport(report),
    hideEntireCurrentUnit: hasFutureRangeTypeSupport(report),
    rangeTypeOptions: limitedRangeTypeOptions
  }));
  const dateRangeLabel = /*#__PURE__*/_jsx(DateRangeLabel, {
    dateRange: fromJS(completeDateRange),
    useFiscalYear: useFiscalYear
  });
  const customWidgetType = getCustomWidgetType(report);
  const displayParams = getDisplayParams(report);
  const editorLabelKey = hasSecondaryDateRangeEditor(customWidgetType, displayParams) ? 'settingLabels.dateRange.pipelineChangeRange' : 'settingLabels.dateRange.label';
  if (isV2FilterExperience) {
    const buttonContent = /*#__PURE__*/_jsx(DateRangeLabel, {
      dateRange: getDateRangeFilterValue(reportConfig),
      useFiscalYear: getUseFiscalYear(reportConfig)
    });
    const toolTipContent = () => {
      return /*#__PURE__*/_jsxs("div", {
        children: [/*#__PURE__*/_jsx(FormattedMessage, {
          message: editorLabelKey
        }), /*#__PURE__*/_jsx("br", {}), /*#__PURE__*/_jsx("b", {
          children: buttonContent
        })]
      });
    };
    const popoverBody = /*#__PURE__*/_jsx(View, {
      inlineSize: "300px",
      paddingBlockEnd: "500",
      children: dateRangeInput
    });
    return /*#__PURE__*/_jsx(BasePopoverEditorComponent, {
      buttonContent: buttonContent,
      dataTestId: "open-date-range-filter-button",
      hasSelectedValue: true,
      tooltipContent: toolTipContent(),
      popoverBody: popoverBody
    });
  }
  return /*#__PURE__*/_jsx(FormControl, {
    label: /*#__PURE__*/_jsx(FormattedMessage, {
      message: editorLabelKey
    }),
    children: readOnly ? dateRangeLabel : dateRangeInput
  });
};
DateRangeEditor.isUsableForReport = report => {
  if (!isConfigReport(report) && !isReportWithPlugins(report)) {
    return false;
  }
  const reportConfig = getReportConfig(report);
  const dateRangeFilterValue = getDateRangeFilterValue(reportConfig);
  return dateRangeFilterValue != null;
};
DateRangeEditor.isDateEditor = true;
DateRangeEditor.isFilterEditor = true;
DateRangeEditor.propTypes = {
  appUsageTracker: PropTypes.object,
  report: ReportType.isRequired,
  onChange: PropTypes.func.isRequired,
  options: PropTypes.shape({
    readOnly: PropTypes.bool
  }).isRequired,
  trackingAssetType: PropTypes.string,
  trackingApplicationName: PropTypes.string,
  trackingScreenName: PropTypes.string
};
export default DateRangeEditor;